import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  toApiError,
  useApi,
  type ApiError,
  type PropertyCommunicationPreviewRequest,
  type PropertyCommunicationPreviewResponse,
} from '@/shared/api';
import { previewProblem } from './catalogProblem';

const DEBOUNCE_MS = 500;

/**
 * Vorschau vom Server, wie beim echten Versand — mit Beispielwerten statt Gastdaten. Gefragt wird
 * einmal je Fassung und beim Tippen erst eine halbe Sekunde nach der letzten Eingabe; bis die neue
 * da ist, und solange die Fassung einen Befund hat, bleibt die letzte gültige stehen und nennt den
 * Grund.
 */
export function useMessagePreview(
  groupId: string,
  request: PropertyCommunicationPreviewRequest,
  /** Befund am Entwurf: dann wird nicht gefragt, weil der Server ablehnen würde. */
  blocked: string | null,
) {
  const api = useApi();
  const slot = `${request.key}:${request.channel}`;
  const version = blocked ? null : JSON.stringify(request);
  const [asked, setAsked] = useState({ slot, version });
  const ask = () =>
    setAsked((current) =>
      current.slot === slot && current.version === version ? current : { slot, version },
    );
  // Ein anderer Anlass oder Kanal ist keine Eingabe: dessen Vorschau kommt ohne Wartezeit.
  if (asked.slot !== slot) ask();
  useEffect(() => {
    const timer = setTimeout(ask, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ask liest genau slot und version
  }, [slot, version]);

  const query = useQuery<PropertyCommunicationPreviewResponse, ApiError>({
    queryKey: ['property-groups', groupId, 'guest-experience', 'preview', asked.version],
    queryFn: async () => {
      try {
        return await api.propertyGroups.guestExperience.messages.preview(
          groupId,
          JSON.parse(asked.version!) as PropertyCommunicationPreviewRequest,
        );
      } catch (error) {
        throw await toApiError(error);
      }
    },
    enabled: asked.version !== null,
    staleTime: Infinity,
    retry: false,
    // Offline pausiert TanStack sonst still, und die alte Fassung sähe aktuell aus.
    networkMode: 'always',
  });

  // Die letzte gültige Fassung je Anlass und Kanal — nie die eines anderen Anlasses.
  const current = asked.slot === slot && asked.version === version;
  const last = useRef(new Map<string, PropertyCommunicationPreviewResponse>());
  if (query.data && current) last.current.set(slot, query.data);
  const shown = (current ? query.data : undefined) ?? last.current.get(slot) ?? null;

  const problem = current && query.isError ? previewProblem(query.error) : null;
  return {
    preview: shown,
    updating: !blocked && (!current || query.isFetching),
    reason: blocked ?? problem?.reason ?? null,
    failed: !!problem,
    retry: problem?.retry ? () => void query.refetch() : null,
  };
}
